/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 07/11/24
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Tile } from '@/components/Tile';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    Flex,
    Heading,
    Radio,
    RadioGroup,
    Stack,
    Text,
    useDisclosure,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { useGetRequest, usePutRequest } from '@/utils/useStatefulRequests';
import {
    mapWorkplanStageToString,
    mapWorkplanStateToString,
    StateToStage,
    UpdateWorkplanStateDto,
    Workplan,
    WorkplanStage,
    workplanStageHelpText,
    WorkplanState,
} from '@/wpt-lib/interfaces/Workplan';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { LabeledSelect } from '@/components/FormInputField';
import { Dialog } from '@/components/Dialog';
import { WorkplanComment, WorkplanCommentSection } from '@/wpt-lib/interfaces/WorkplanComment';

const isEOYRatingGiven = (currentVersion: Workplan['currentVersion']) =>
    !!(
        currentVersion.overallEndOfYearRating &&
        currentVersion.teachingEndOfYearRating &&
        currentVersion.researchEndOfYearRating &&
        currentVersion.serviceEndOfYearRating
    );

const WorkplanStatusEditor = ({
    assignment,
    currentAssignment,
    refresh,
}: {
    assignment: Assignment;
    currentAssignment: Assignment;
    refresh: () => void;
}) => {
    const { makeRequest, loading, responseData: workplans, error } = useGetRequest<Workplan[]>();
    const [selectedWorkplanId, setSelectedWorkplanId] = useState<number>();
    const [toState, setToState] = useState<WorkplanState>();
    const [toStage, setToStage] = useState<WorkplanStage>();
    const [refreshCurrentWorkplans, setRefreshCurrentWorkplans] = useState(false);
    const [preConditionsError, setPreConditionsError] = useState<string>();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    useAsyncEffect(() => {
        (async () => {
            const response = await makeRequest(`/workplan/current/${assignment.id}`, {
                params: { assignment: currentAssignment.id },
            });

            if (!selectedWorkplanId) {
                for (const workplan of response.data) {
                    if (workplan.year === new Date().getFullYear()) {
                        setSelectedWorkplanId(+workplan.id);
                        break;
                    }
                }
            }
        })();
    }, [assignment.id, currentAssignment.id, refresh, refreshCurrentWorkplans]);

    const selectedWorkplan = workplans?.find((workplan) => workplan.id === selectedWorkplanId) || workplans?.[0];

    const setState = (state: WorkplanState | undefined) => {
        setToState(state);
        setPreConditionsError(undefined);
    };

    useEffect(() => {
        if (selectedWorkplan) {
            const initialState = getWorkplanStates()[0];
            setState(initialState);
            setStageForState(initialState);
        }
    }, [selectedWorkplan]);

    const {
        loading: isUpdating,
        error: updatingWorkplanError,
        makeRequest: updateWorkplan,
    } = usePutRequest<unknown, UpdateWorkplanStateDto>();

    const { makeRequest: eoyCommentRequest, loading: eoyCommentsLoading } = useGetRequest<WorkplanComment[]>();

    const checkStateChangePreconditions = useCallback(async () => {
        try {
            if (!(selectedWorkplan && isEOYRatingGiven(selectedWorkplan?.currentVersion))) {
                setPreConditionsError(
                    `You have to give end of year rating for this employee before endorsing workplan.`,
                );
                return false;
            }
            const { data: eoyComments } = await eoyCommentRequest(`/workplan/${selectedWorkplan?.id}/comment`, {
                params: {
                    section: WorkplanCommentSection.END_YEAR,
                    version: selectedWorkplan.currentVersion.version,
                    assignment: currentAssignment.id,
                },
            });

            if (!eoyCommentsLoading && eoyComments?.length === 0) {
                setPreConditionsError(
                    'You must add an end of year comment for this employee before endorsing workplan.',
                );
                return false;
            }

            return true;
        } catch (e) {
            //handle by the hook
            console.error('Error fetching EOY comments:', e);
        }
    }, [selectedWorkplan]);

    const updateWorkplanWithStateAndStage = useCallback(async () => {
        try {
            if (!selectedWorkplan || !toState || !toStage) return;
            if (toState === WorkplanState.ENDORSED && !(await checkStateChangePreconditions())) {
                return;
            }
            await updateWorkplan(
                `workplan/${selectedWorkplan.id}`,
                { state: toState, stage: toStage },
                { params: { assignment: currentAssignment.id } },
            );
            toast({
                title: 'Workplan Updated',
                description: `The workplan status has been updated to "${mapWorkplanStateToString[toState]}" and the stage to "${mapWorkplanStageToString[toStage]}".`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });

            setRefreshCurrentWorkplans(!refreshCurrentWorkplans);
            setState(undefined);
            setToStage(undefined);
            refresh();
            onClose();
        } catch (e) {
            //handle by the hook
            console.error('Error updating workplan:', e);
        }
    }, [selectedWorkplan, toState, toStage, currentAssignment.id]);

    const setStageForState = useCallback(
        (state: WorkplanState) => {
            const stages = getStagesForState(state);
            setToStage(stages[0]);
        },
        [toState, selectedWorkplan],
    );

    const getWorkplanStates = useCallback(() => {
        const currentState = selectedWorkplan?.currentVersion.state;
        if (!currentState) return [];

        return Object.values(WorkplanState).filter((state) => {
            const stagesForCurrentState = getStagesForState(currentState);
            return stagesForCurrentState.length === 1 || state !== currentState;
        });
    }, [selectedWorkplan]);

    const getStagesForState = useCallback(
        (state: WorkplanState) => {
            const currentStage = selectedWorkplan?.currentVersion.stage;
            const stages = StateToStage[state];
            const stageArray = Array.isArray(stages) ? stages : [stages];

            return state === selectedWorkplan?.currentVersion.state && currentStage
                ? stageArray.filter((stage) => stage !== currentStage)
                : stageArray;
        },
        [selectedWorkplan],
    );

    const showStageSelection = (stages: WorkplanStage[]) =>
        stages.includes(WorkplanStage.PART_YEAR) || stages.includes(WorkplanStage.END_YEAR);

    return (
        <Tile
            colspan={2}
            collapsable
            overflowX="auto"
            heading={
                <Heading size="sm" textColor={'blue.500'}>
                    {'Probation Plan and Workplan \u2014 Status and Stages'}
                </Heading>
            }
            defaultOpen
        >
            <VStack alignItems="flex-start" gap={3}>
                <Text>
                    This section allows you to update the status and stage of workplans in accordance with the workflow
                    policies. Select a workplan from the available years, choose the desired status and stage, and apply
                    changes.
                </Text>
                {workplans?.length === 0 && (
                    <Text as="span" fontWeight="normal" fontStyle="italic" color="gray">
                        There are currently no workplans available to update
                    </Text>
                )}
                {selectedWorkplan && (
                    <StateDisplay
                        loading={loading}
                        error={!!error}
                        errorRender={() => <SimpleAlert variant="subtle">Could not load workplan years</SimpleAlert>}
                        data={workplans}
                        render={(data) => (
                            <VStack alignItems="flex-start" gap={4}>
                                <Flex alignItems="center" justifyContent="center" gap={3}>
                                    <LabeledSelect
                                        label="Year:"
                                        labelProps={{ fontWeight: 'bold' }}
                                        onChange={(ev) => setSelectedWorkplanId(parseInt(ev.target.value))}
                                        value={selectedWorkplan?.id}
                                        minW="140px"
                                        width="fit-content"
                                        controlProps={{ width: 'inherit' }}
                                    >
                                        {data
                                            .sort((w1, w2) => w1.year - w2.year)
                                            .map((workplan) => (
                                                <option value={workplan.id} key={workplan.id}>
                                                    {workplan.year}
                                                </option>
                                            ))}
                                    </LabeledSelect>
                                    <Flex alignItems="center" justifyContent="center" gap={3}>
                                        <Heading size="sm" fontWeight="bold" as="h3">
                                            WP Type:
                                        </Heading>
                                        <Text>
                                            {selectedWorkplan.currentVersion.probationPlan
                                                ? 'Probation Plan'
                                                : 'Annual Workplan'}
                                        </Text>
                                    </Flex>
                                    <Flex alignItems="center" justifyContent="center" gap={3}>
                                        <Heading size="sm" fontWeight="bold" as="h3">
                                            Status:
                                        </Heading>
                                        <Text>{mapWorkplanStateToString[selectedWorkplan.currentVersion.state]}</Text>
                                    </Flex>
                                    <Flex alignItems="center" justifyContent="center" gap={3}>
                                        <Heading size="sm" fontWeight="bold" as="h3">
                                            Stage:
                                        </Heading>
                                        <Text>{mapWorkplanStageToString[selectedWorkplan.currentVersion.stage]}</Text>
                                    </Flex>
                                </Flex>
                                <Box>
                                    <LabeledSelect
                                        label="Change Status to:"
                                        labelProps={{ fontWeight: 'bold' }}
                                        onChange={(ev) => {
                                            const selectedState = ev.target.value as WorkplanState;
                                            setState(selectedState);
                                            setStageForState(selectedState);
                                        }}
                                        value={toState || ''}
                                        minW="140px"
                                        width="fit-content"
                                        controlProps={{ width: 'inherit' }}
                                    >
                                        {getWorkplanStates().map((state) => (
                                            <option value={state} key={state}>
                                                {mapWorkplanStateToString[state]}
                                            </option>
                                        ))}
                                    </LabeledSelect>
                                </Box>
                                {toState && showStageSelection(getStagesForState(toState)) && (
                                    <Box>
                                        <Heading size="sm" fontWeight="bold" mb={2} as="h3">
                                            Change Stage to:
                                        </Heading>
                                        <RadioGroup
                                            onChange={(value) => setToStage(value as WorkplanStage)}
                                            value={toStage}
                                        >
                                            <Stack direction="column">
                                                {getStagesForState(toState).map((stage) => (
                                                    <Radio value={stage} key={stage}>
                                                        {`${mapWorkplanStageToString[stage]} - ${
                                                            workplanStageHelpText[
                                                                stage as
                                                                    | WorkplanStage.PART_YEAR
                                                                    | WorkplanStage.END_YEAR
                                                            ]
                                                        }`}
                                                    </Radio>
                                                ))}
                                            </Stack>
                                        </RadioGroup>
                                    </Box>
                                )}

                                {toState === WorkplanState.ENDORSED && (
                                    <Alert status="warning" variant="subtle">
                                        <AlertIcon />
                                        <AlertDescription>
                                            Warning: This approves the workplan and is final. Please ensure this is only
                                            selected if the supervisor has endorsed the workplan with a performance
                                            rating.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {toState === WorkplanState.HISTORICAL && (
                                    <Alert status="warning" variant="subtle">
                                        <AlertIcon />
                                        <AlertDescription>
                                            Warning: Historical workplans cannot be reversed or edited. Goals within
                                            historical workplans can be copied to other workplan years, but not comments
                                            or progress updates.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                <Dialog
                                    isOpen={isOpen}
                                    isLoading={isUpdating}
                                    dialogHeader="Confirm Workplan Update"
                                    dialogConfirmLabel="Apply Changes"
                                    onClose={onClose}
                                    dialogConfirmDisabled={loading || !toState || !toStage || isUpdating}
                                    colorScheme="blue"
                                    errorMsg={
                                        updatingWorkplanError
                                            ? 'There was an issue updating the workplan. Please refresh the page and try again.'
                                            : undefined
                                    }
                                    dialogAction={() => updateWorkplanWithStateAndStage()}
                                >
                                    {toState && (
                                        <>
                                            {preConditionsError && (
                                                <SimpleAlert variant={'subtle'}>{preConditionsError}</SimpleAlert>
                                            )}
                                            <Text>
                                                Are you sure you want to update the workplan for{' '}
                                                <strong>{selectedWorkplan?.year}</strong> to
                                                <strong> {mapWorkplanStateToString[toState]}</strong> status
                                                {toStage &&
                                                    ![WorkplanStage.PLANNING, WorkplanStage.CLOSED].includes(
                                                        toStage,
                                                    ) && (
                                                        <>
                                                            {' '}
                                                            and <strong>
                                                                {mapWorkplanStageToString[toStage]}
                                                            </strong>{' '}
                                                            stage
                                                        </>
                                                    )}
                                                ?
                                            </Text>
                                        </>
                                    )}
                                </Dialog>
                                <Button
                                    colorScheme="blue"
                                    maxWidth="160"
                                    onClick={onOpen}
                                    disabled={
                                        loading ||
                                        !toState ||
                                        !toStage ||
                                        assignment.inactive ||
                                        !!preConditionsError ||
                                        eoyCommentsLoading
                                    }
                                    isLoading={loading || eoyCommentsLoading || isUpdating}
                                >
                                    Update Workplan
                                </Button>
                            </VStack>
                        )}
                    />
                )}
            </VStack>
        </Tile>
    );
};

export default WorkplanStatusEditor;
